import { useCallback, useState } from 'react'

import { useTelemetrySource } from '../../lib/api/source.tsx'
import { AthenaError } from '../../lib/api/client.ts'
import { machines } from '../../lib/api/resources.ts'
import { hasRecommendedIsolation } from '../../lib/commands/isolation.ts'
import { formatDayClock } from '../../lib/format.ts'
import { scannerIpOf } from '../../lib/ip/semantics.ts'
import { FleetIpDialog } from '../machines/FleetIp.tsx'
import { IsolateHostDialog, machineNetworkOf, RecommendedIsolation } from '../machines/IsolateHost.tsx'
import { IpCommandDialog } from '../machines/MachineIps.tsx'
import { agentHref, processHref } from '../../lib/logs/investigation.ts'
import { navigate } from '../../lib/router.tsx'
import type { DetectionAlert, LogDetail } from '../../lib/sse/types.ts'
import { useResource } from '../../lib/useResource.ts'
import { DossierBody } from '../logs/EventDossier.tsx'
import { Button } from '../primitives/Button.tsx'
import { Drawer } from '../primitives/Drawer.tsx'
import { ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { RiskMeter, SeverityBadge } from './Severity.tsx'

/**
 * A ficha de um alerta. O corpo é a MESMA ficha de evento do console de logs —
 * com o bloco do alerta (regra, MITRE, explicação, evidências) no topo —, para
 * um alerta aberto daqui e um aberto pelo histórico serem a mesma leitura.
 */
export function DetectionDrawer({
  alert,
  onClose,
  onFilterRule,
}: {
  alert: DetectionAlert
  onClose: () => void
  onFilterRule?: (ruleId: string) => void
}) {
  const source = useTelemetrySource()
  const fetcher = useCallback(
    (signal: AbortSignal) => source.logDetail(alert.row, signal),
    [source, alert.row],
  )
  const { data, error } = useResource<LogDetail>(fetcher)
  const gone = error instanceof AthenaError && error.code === 'log_not_found'
  const host = alert.row.hostname ?? alert.host_hostname ?? alert.row.endpoint_id

  // Atalhos de IP só em alerta de varredura, e só sobre um IP de verdade
  // (`data.key`, ou o `target.ip` do block_ip) — nunca sobre texto arbitrário.
  // Comandos são da Black Ghost: na experiência de cliente o backend responderia
  // 403, então as ações nem aparecem.
  const scanner = source.multiTenant
    ? scannerIpOf({
        rule_id: alert.rule_id,
        response: alert.response,
        key: data?.details.alert?.key ?? null,
      })
    : null
  const [ipAction, setIpAction] = useState<'unblock' | 'allow' | 'fleet' | null>(null)

  // Isolamento RECOMENDADO: o agente não isolou e pede uma decisão. Só a Black
  // Ghost decide (o cliente vê o rótulo, sem ação). A rede da máquina é lida
  // para não oferecer isolar uma máquina que já está isolada.
  const recommended = source.multiTenant && hasRecommendedIsolation(alert.response)
  const machineFetcher = useCallback(
    (signal: AbortSignal) => (recommended ? machines.get(alert.row.endpoint_id, signal) : Promise.resolve(null)),
    [recommended, alert.row.endpoint_id],
  )
  const machine = useResource(machineFetcher)
  const [isolating, setIsolating] = useState(false)

  const go = (href: string) => {
    onClose()
    navigate(href)
  }

  return (
    <Drawer
      open
      onOpenChange={(open) => !open && onClose()}
      title={<span className="block truncate">{alert.rule_name ?? alert.rule_id ?? 'Alerta'}</span>}
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="tnum">{formatDayClock(alert.row.event_time)} UTC</span>
          <span aria-hidden>·</span>
          <span className="truncate font-mono">{host}</span>
          {source.multiTenant && alert.row.tenant_name !== null && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{alert.row.tenant_name}</span>
            </>
          )}
        </span>
      }
      meta={
        <span className="flex items-center gap-2">
          <RiskMeter score={alert.risk_score} compact />
          <SeverityBadge severity={alert.severity} />
        </span>
      }
      footer={
        <>
          {alert.process_entity_id !== null && (
            <Button variant="primary" size="sm" onClick={() => go(processHref(alert.process_entity_id!))}>
              Linha do tempo do processo
            </Button>
          )}
          <Button size="sm" onClick={() => go(agentHref(alert.row.endpoint_id, {}))}>
            Logs da máquina
          </Button>
          {scanner !== null && (
            <>
              <Button size="sm" variant="danger" onClick={() => setIpAction('unblock')}>
                Desbloquear este IP
              </Button>
              <Button size="sm" onClick={() => setIpAction('fleet')}>
                Bloquear em toda a empresa
              </Button>
              <Button size="sm" onClick={() => setIpAction('allow')}>
                Permitir
              </Button>
            </>
          )}
          {alert.rule_id !== null && onFilterRule !== undefined && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onFilterRule(alert.rule_id!)
                onClose()
              }}
            >
              Só esta regra
            </Button>
          )}
        </>
      }
    >
      {recommended && (
        <div className="px-4 pt-3">
          <RecommendedIsolation
            network={
              machine.error !== null
                ? 'unknown'
                : machine.data === null
                  ? 'checking'
                  : machineNetworkOf(machine.data.isolation)
            }
            isolatedSince={machine.data?.isolation?.isolated_since ?? null}
            onIsolate={() => setIsolating(true)}
          />
        </div>
      )}

      {gone ? (
        <div className="px-4 py-3">
          <Notice tone="neutral">O evento do alerta não existe mais ou saiu por retenção.</Notice>
        </div>
      ) : error !== null ? (
        <div className="px-4 py-3">
          <ErrorState error={error} />
        </div>
      ) : data === null ? (
        <LoadingRow label="Carregando o alerta…" />
      ) : (
        <DossierBody row={alert.row} detail={data} onNavigate={onClose} />
      )}

      {scanner !== null && (ipAction === 'unblock' || ipAction === 'allow') && (
        <IpCommandDialog
          machine={{ id: alert.row.endpoint_id, label: host }}
          mode={ipAction}
          target={scanner}
          onClose={() => setIpAction(null)}
          onSettled={() => {}}
        />
      )}
      {isolating && (
        <IsolateHostDialog
          machine={{ id: alert.row.endpoint_id, label: host }}
          onClose={() => setIsolating(false)}
          onIsolated={machine.reload}
        />
      )}
      {scanner !== null && ipAction === 'fleet' && (
        <FleetIpDialog
          companyId={alert.row.tenant_id}
          companyName={alert.row.tenant_name ?? alert.row.tenant_id}
          mode="block"
          target={scanner}
          onClose={() => setIpAction(null)}
        />
      )}
    </Drawer>
  )
}
